import {AbstractType, Injectable, NgZone, Type} from "@angular/core";
import {SortDirection} from "@angular/material/sort";

import {ControlOption} from "framework/ui/control-option";
import {Future} from "framework/utils/concurrency";

import * as rx from "rxjs";

export function throwMissingSwitchCase(p: never): never;
export function throwMissingSwitchCase<T>(p: T)
{
    throw new Error("Missing switch case: " + p);
}

export const ALLOWED_EMAIL_PATTERN: RegExp =
                 /^([\w.-]+)@(\[(\d{1,3}\.){3}|(?!(Aol|gmail|hotmail|iCloud|live|mail|outlook|protonmail|rediff|yahoo|yandex))(([a-z\d-]+\.)+))([a-z]{2,10}|\d{1,3})(\]?)$/;

export const ALLOWED_WEBHOOK_PATTERN: RegExp =
                 /^http[s]?:\/\/(?:[a-zA-Z]|[0-9]|[$-_@.&+]|[!*\\(\\),]|(?:%[0-9a-fA-F][0-9a-fA-F]))+$/;

const COMPACT_NUMBER_TIERS: ReadonlyArray<{readonly threshold: number; readonly suffix: string}> = [
    {threshold: 1_000_000_000, suffix: "B"},
    {threshold: 1_000_000, suffix: "M"},
    {threshold: 1_000, suffix: "K"}
];

@Injectable({providedIn: "root"})
export class UtilsService
{
    public static showDebug: boolean;
    private static readonly s_enumValueCache = new WeakMap<object, Set<string>>();

    /**
     * Constructor
     */
    constructor(readonly ngZone: NgZone)
    {
    }

    public static newObserver<T>(next: (value: T) => void,
                                 error?: (err: any) => void,
                                 complete?: () => void): Partial<rx.Observer<T>>
    {
        const observer: Partial<rx.Observer<T>> = {};
        if (next) observer.next = next;
        if (error) observer.error = error;
        if (complete) observer.complete = complete;
        return observer;
    }

    public static asTyped<S, T extends S>(field: S,
                                          type: AbstractType<T>): T
    {
        if (field instanceof type)
        {
            return field;
        }

        return null;
    }

    public static asArray<T>(input: T | T[],
                             nullFallback?: boolean): T[]
    {
        if (Array.isArray(input)) return input;
        if (input) return [input];
        return nullFallback ? null : [];
    }

    public static arrayCopy<T>(a: T[] | readonly T[] | null): T[]
    {
        return [...(a || [])];
    }

    public static arraysSameLength<T>(a: T[],
                                      b: T[]): boolean
    {
        a ||= [];
        b ||= [];
        return a.length === b.length;
    }

    public static validateProperty(value: string,
                                   minLength?: number,
                                   maxLength?: number)
    {
        if (value == null)
        {
            return false;
        }

        if (UtilsService.isNumber(minLength) && value.length < minLength)
        {
            return false;
        }

        if (UtilsService.isNumber(maxLength) && value.length > maxLength)
        {
            return false;
        }

        return true;
    }

    private static readonly s_htmlEscapeMap: Lookup<string> = {
        "&" : "&amp;",
        "<" : "&lt;",
        ">" : "&gt;",
        "\"": "&quot;",
        "'" : "&#39;"
    };

    public static escapeHtml(value: string): string
    {
        return (value ?? "").replace(/[&<>"']/g, (char) => UtilsService.s_htmlEscapeMap[char] ?? char);
    }

    public static arraysEquivalent<T>(a: T[],
                                      b: T[],
                                      areEqual = (v1: T,
                                                  v2: T) => v1 === v2): boolean
    {
        if (!UtilsService.arraysSameLength(a, b)) return false;

        if (a)
        {
            for (let i = 0; i < a.length; i++)
            {
                if (!areEqual(a[i], b[i])) return false;
            }
        }

        return true;
    }

    /**
     * returns (a and b are falsy) || (same lengths && all contents of a are also in b and vis versa)
     *
     * @param a
     * @param b
     */
    public static compareArraysAsSets<T>(a: T[],
                                         b: T[]): boolean
    {
        if (!UtilsService.arraysSameLength(a, b)) return false;

        if (a && b)
        {
            let aLookup = new Set<T>(a);
            for (let bItem of b)
            {
                if (!aLookup.has(bItem)) return false;
            }

            let bLookup = new Set<T>(b);
            for (let aItem of a)
            {
                if (!bLookup.has(aItem)) return false;
            }
        }

        return true;
    }

    public static isInArray<T>(array: T[],
                               value: T,
                               insideIfEmpty: boolean): boolean
    {
        if (!array?.length) return insideIfEmpty;

        return array.indexOf(value) >= 0;
    }

    public static zipArrays<S, T>(a: S[],
                                  b: T[]): [S, T][]
    {
        let res: [S, T][] = [];
        for (let i = 0; i < Math.max(a.length, b.length); i++)
        {
            res.push([
                         a[i],
                         b[i]
                     ]);
        }

        return res;
    }

    public static dedupeElements<T>(array: T[]): T[]
    {
        return Array.from(new Set(array));
    }

    public static flattenArray<T>(array: T[][]): T[]
    {
        const res: T[] = [];
        if (array)
        {
            for (let i = 0; i < array.length; i++)
            {
                res.push(...array[i]);
            }
        }
        return res;
    }

    public static setDifference<T>(setA: Set<T>,
                                   setB: Set<T>): T[]
    {
        return [...setA].filter((a) => !setB.has(a));
    }

    public static compareSets<T>(setA: Set<T>,
                                 setB: Set<T>): { same: Set<T>, notInA: Set<T>, notInB: Set<T> }
    {
        let same   = new Set<T>();
        let notInA = new Set<T>();
        let notInB = new Set<T>();

        for (let e of setA)
        {
            if (setB.has(e))
            {
                same.add(e);
            }
            else
            {
                notInB.add(e);
            }
        }

        for (let e of setB)
        {
            if (!setA.has(e))
            {
                notInA.add(e);
            }
        }

        return {
            same  : same,
            notInA: notInA,
            notInB: notInB
        };
    }

    public static extractLookup<T extends Identified>(items: T[],
                                                      lookup?: Lookup<T>): Lookup<T>
    {
        return this.extractMappedLookup(items, (item) => item, lookup);
    }

    public static extractMappedLookup<T extends Identified, U>(items: T[],
                                                               mappingFn: (item: T) => U,
                                                               lookup?: Lookup<U>): Lookup<U>
    {
        return this.extractLookupWithMapping(items, (v) => v.id + "", mappingFn, lookup);
    }

    public static extractLookupWithMapping<T, U, K extends EnumOrString>(items: T[],
                                                                         mappingKeyFn: (item: T) => K,
                                                                         mappingValueFn: (item: T) => U,
                                                                         lookup?: Lookup<U, K>): Lookup<U, K>
    {
        if (!lookup)
        {
            lookup = {};
        }

        for (let item of items || [])
        {
            if (item)
            {
                lookup[mappingKeyFn(item)] = mappingValueFn(item);
            }
        }

        return lookup;
    }

    public static mapLookup<T, U>(lookup: Lookup<T>,
                                  mapFn: (key: string,
                                          value: T) => U,
                                  noNulls: boolean): U[]
    {
        const result = [];
        for (const id in lookup || {})
        {
            const curr = mapFn(id, lookup[id]);
            if (!noNulls || curr != null) result.push(curr);
        }
        return result;
    }

    public static extractMap<T, U>(data: U[],
                                   mappingKeyFn: (item: U) => T): Map<T, U>
    {
        const result = new Map<T, U>();

        for (const entry of data)
        {
            result.set(mappingKeyFn(entry), entry);
        }

        return result;
    }

    public static mapIterable<S, T>(set: Iterable<S>,
                                    callback: (item: S) => T): T[]
    {
        let result: T[] = [];
        for (let item of set)
        {
            result.push(callback(item));
        }

        return result;
    }

    public static getRoundedValue(value: number,
                                  numDecimals?: number): number
    {
        if (!UtilsService.isNumber(value)) return null;
        return parseFloat(value.toFixed(numDecimals || 0));
    }

    public static formatCompactNumber(value: number,
                                      maxFractionDigits = 2): string
    {
        if (!Number.isFinite(value) || !value) return "0";

        const formatter = new Intl.NumberFormat("en-US", {minimumFractionDigits: 0, maximumFractionDigits: maxFractionDigits});

        const tier = COMPACT_NUMBER_TIERS.find((t) => Math.abs(value) >= t.threshold);
        if (!tier) return formatter.format(value);

        return `${formatter.format(value / tier.threshold)}${tier.suffix}`;
    }

    public static countDecimals(value: number): number
    {
        if (Math.floor(value.valueOf()) === value.valueOf()) return 0;

        const str = value.toString();

        if (str.indexOf("e") !== -1)
        {
            const split    = str.split("e");
            const exponent = parseInt(split[1]);
            const base     = split[0].split(".");
            const decimal  = base[1].length;
            if (exponent >= 0)
            {
                return Math.max(exponent - decimal, 0);
            }
            else
            {
                return (exponent * -1) + decimal;
            }
        }
        else if (str.indexOf(".") !== -1 && str.indexOf("-") !== -1)
        {
            return Number.parseInt(str.split("-")[1]) || 0;
        }
        else if (str.indexOf(".") !== -1)
        {
            return str.split(".")[1].length || 0;
        }

        return Number.parseInt(str.split("-")[1]) || 0;
    }

    public static getEffectivePercent(percent: number,
                                      numDecimals?: number): number
    {
        if (!UtilsService.isNonNegativeNumber(percent)) return null;

        if (numDecimals)
        {
            percent = UtilsService.getRoundedValue(percent, numDecimals);
        }
        else
        {
            if (percent < 1)
            {
                percent = Math.ceil(percent);
            }
            else if (percent > 99)
            {
                percent = Math.floor(percent);
            }
            else
            {
                percent = Math.round(percent);
            }
        }
        return UtilsService.clamp(0, 100, percent);
    }

    /** Splits a scaled billing unit such as "1K tokens" into its multiplier (1000) and base unit ("tokens"); null when unscaled. */
    public static parseScaledUnit(unit: string): { multiplier: number; baseUnit: string } | null
    {
        const match = /^\s*(\d+(?:\.\d+)?)\s*([KMB])\s+(\S.*)$/i.exec(unit ?? "");
        if (!match)
        {
            return null;
        }

        const scale: Record<string, number> = {
            K: 1e3,
            M: 1e6,
            B: 1e9
        };

        return {
            multiplier: +match[1] * scale[match[2].toUpperCase()],
            baseUnit  : match[3].trim()
        };
    }

    public static isNumber(value: unknown): value is number
    {
        return typeof value == "number" && !isNaN(value);
    }

    public static isFiniteNumber(value: unknown): value is number
    {
        return UtilsService.isNumber(value) && Number.isFinite(value);
    }

    /**
     * Unlike {@link isNumber}, this inspects the *text* of a string: it answers whether the user typed something that
     * is nothing but a number ("12", "12.5", "-5", "1e3"). Text that merely contains digits ("env-2", "1a") is not.
     */
    public static isNumericString(value: string): boolean
    {
        const trimmed = value?.trim();
        return !!trimmed && Number.isFinite(Number(trimmed));
    }

    public static isPositiveNumber(value: unknown): value is number
    {
        return UtilsService.isNumber(value) && value > 0;
    }

    public static isNonNegativeNumber(value: unknown): value is number
    {
        return UtilsService.isNumber(value) && value >= 0;
    }

    public static isFinitePositiveNumber(value: unknown): value is number
    {
        return UtilsService.isFiniteNumber(value) && value > 0;
    }

    public static isFiniteNonNegativeNumber(value: unknown): value is number
    {
        return UtilsService.isFiniteNumber(value) && value >= 0;
    }

    public static stringMatchesRegex(source: string,
                                     regex: string,
                                     caseSensitive: boolean): boolean
    {
        return !!source?.match(new RegExp(regex, caseSensitive ? undefined : "i"));
    }

    public static isValidRegex(regex: string): boolean
    {
        try
        {
            new RegExp(regex);
            return true;
        }
        catch (e)
        {
            return false;
        }
    }


    public static isBlankString(a: string): boolean
    {
        return !a || a.length == 0 || a.trim().length == 0;
    }

    public static equivalentStrings(a: string,
                                    b: string): boolean
    {
        return !UtilsService.compareStringsHelper(a, b);
    }

    public static flipSortDirection(direction: SortDirection): SortDirection
    {
        return direction === "asc" ? "desc" : "asc";
    }

    public static sortByLookupFn<T>(lookup: Lookup<number>,
                                    getLookupKey: (item: T) => EnumOrString,
                                    ascending: boolean): (a: T,
                                                          b: T) => number
    {
        return (a: T,
                b: T) =>
        {
            return UtilsService.compareWith(a, b, (item: T) => lookup[getLookupKey(item)], ascending);
        };
    }

    public static compareHierarchical<T>(a: T,
                                         b: T,
                                         orderedComparators: ((a: T,
                                                               b: T) => number)[]): number
    {
        // Return first non-zero result
        for (const comparator of orderedComparators)
        {
            const result = comparator(a, b);
            if (result !== 0) return result;
        }

        return 0;
    }

    public static compareWith<T>(a: T,
                                 b: T,
                                 getValue: (item: T) => number,
                                 ascending: boolean): number
    {
        return ascending ? getValue(a) - getValue(b) : getValue(b) - getValue(a);
    }

    /** Case-insensitive substring match, falsy-safe. Extracted 2026-08-07 from two independent,
     *  byte-for-byte-identical private copies (ai-services-table.component.ts and
     *  frontier-ai-resource-table.component.ts) — both free-text row filters need exactly this. */
    public static containsIgnoreCase(value: string | undefined | null,
                                     needle: string): boolean
    {
        return !!value && value.toLowerCase()
                               .includes(needle);
    }

    /** De-duplicated, locale-sorted values — the shape every "distinct column values for a filter
     *  dropdown" helper in this app needs. Extracted 2026-08-07 alongside containsIgnoreCase, same
     *  two call sites. */
    public static distinctSorted(values: string[]): string[]
    {
        return Array.from(new Set(values))
                    .sort((a, b) => a.localeCompare(b));
    }

    /** distinctSorted() over a single string-valued field, filtering out falsy values first — the
     *  "distinct column values for a filter dropdown" pattern every single-value column repeats
     *  (e.g. distinctModels/distinctWorkspaces/distinctLocations across the ai-services tables). */
    public static distinctFieldValues<T>(rows: readonly T[],
                                         selector: (row: T) => string | undefined | null): string[]
    {
        return UtilsService.distinctSorted(rows.map(selector)
                                                .filter((v): v is string => !!v));
    }

    /** distinctSorted() over a multi-value (array) field, flattened across every row first — the
     *  same pattern as distinctFieldValues() but for columns like regions/models that hold more
     *  than one value per row. */
    public static distinctFlatValues<T>(rows: readonly T[],
                                        selector: (row: T) => readonly string[] | undefined | null): string[]
    {
        const all: string[] = [];
        for (const row of rows) all.push(...(selector(row) || []));
        return UtilsService.distinctSorted(all);
    }

    public static compareStrings(a: string,
                                 b: string,
                                 ascending: boolean): number
    {
        return ascending ? UtilsService.compareStringsHelper(a, b) : UtilsService.compareStringsHelper(b, a);
    }

    private static compareStringsHelper(a: string,
                                        b: string): number
    {
        a = a || "";
        b = b || "";

        return a.localeCompare(b);
    }

    public static compareNumbers(a: number,
                                 b: number,
                                 ascending: boolean,
                                 nanToEnd?: boolean): number
    {
        let nanResult = nanToEnd ? 1 : -1;
        if (isNaN(a))
        {
            return isNaN(b) ? 0 : nanResult;
        }
        else if (isNaN(b))
        {
            return -nanResult;
        }
        else
        {
            return ascending ? a - b : b - a;
        }
    }

    public static compareJson(a: any,
                              b: any,
                              printDebug: boolean = UtilsService.showDebug): boolean
    {
        let binMatch = UtilsService.compareDeep(a, b);

        let jsonMatch = UtilsService.compareJsonTextual(a, b);
        if (printDebug && jsonMatch != (binMatch == null))
        {
            console.debug(`compare mismatch between JSON and per-field:`);
            console.debug(`a = ${JSON.stringify(a || "")}`);
            console.debug(`b = ${JSON.stringify(b || "")}`);
            console.debug(`compareDeep = ${binMatch}`);
        }

        return !binMatch;
    }

    public static compareDeep(a: any,
                              b: any): string
    {
        let set = new Set<any>();

        return UtilsService.compareRecursively(set, a, b);
    }

    private static compareRecursively(set: Set<any>,
                                      a: any,
                                      b: any): string
    {
        if (a == b)
        {
            return null;
        }

        if (!a)
        {
            return "<no value on left>";
        }

        if (!b)
        {
            return "<no value on right>";
        }

        if (typeof a != typeof b)
        {
            return "<type mismatch>";
        }

        if (typeof a !== "object")
        {
            return "<value mismatch>";
        }

        if (Object.getPrototypeOf(a) !== Object.getPrototypeOf(b))
        {
            return "<class mismatch>";
        }

        if (typeof a.toJSON === "function")
        {
            a = a.toJSON();
            b = b.toJSON();
            return UtilsService.compareRecursively(set, a, b);
        }

        if (set.has(a) || set.has(b)) // Recursion detected, assume different!
        {
            return "<recursion detected>";
        }

        set.add(a);
        set.add(b);

        if (Array.isArray(a))
        {
            if (!Array.isArray(b))
            {
                return "<array vs object>";
            }

            let aLen = a.length;
            let bLen = b.length;

            if (aLen != bLen) return "<array length>";

            for (let i = 0; i < aLen; i++)
            {
                let mismatch = UtilsService.compareRecursively(set, a[i], b[i]);
                if (mismatch)
                {
                    return `[${i}] => ${mismatch}`;
                }
            }
        }
        else
        {
            let aKeys = Object.keys(a);
            let bKeys = Object.keys(b);

            let aKeyLookup = new Set<string>(aKeys);
            for (let key of bKeys)
            {
                if (!aKeyLookup.has(key))
                {
                    return `<left lacks ${key}>`;
                }
            }

            let bKeyLookup = new Set<string>(bKeys);
            for (let key of aKeys)
            {
                if (!bKeyLookup.has(key))
                {
                    return `<right lacks ${key}>`;
                }
            }

            for (let key of aKeys)
            {
                if (key == "scaling") continue;
                let mismatch = UtilsService.compareRecursively(set, a[key], b[key]);
                if (mismatch)
                {
                    return `${key} => ${mismatch}`;
                }
            }
        }

        return null;
    }

    public static diffJson(a: any,
                           b: any,
                           path: string[] = []): Change[]
    {
        if (a == b)
        {
            return [];
        }

        if (typeof a !== typeof b || typeof a !== "object")
        {
            return [
                {
                    path: path,
                    a   : a,
                    b   : b
                }
            ];
        }

        let aKeys = Object.keys(a);
        let bKeys = Object.keys(b);
        aKeys.sort();
        bKeys.sort();
        let i                 = 0;
        let j                 = 0;
        let results: Change[] = [];
        while (i < aKeys.length || j < bKeys.length)
        {
            let aKey = aKeys[i];
            let bKey = bKeys[j];
            let comp = UtilsService.compareStrings(aKey, bKey, true);
            if (i == aKeys.length) comp = 1;
            if (j == bKeys.length) comp = -1;

            if (comp < 0)
            {
                // missing prop
                results.push({
                                 path: [
                                     ...path,
                                     aKey
                                 ],
                                 a   : a[aKey],
                                 b   : null
                             });
                i++;
            }
            else if (comp > 0)
            {
                // extra prop
                results.push({
                                 path: [
                                     ...path,
                                     bKey
                                 ],
                                 a   : null,
                                 b   : b[bKey]
                             });
                j++;
            }
            else
            {
                results.push(...UtilsService.diffJson(a[aKey],
                                                      b[aKey],
                                                      [
                                                          ...path,
                                                          aKey
                                                      ]));
                i++;
                j++;
            }
        }

        return results;
    }

    public static cloneAndRemoveKeys(obj: any,
                                     keysToExclude: string[]): any
    {
        if (Array.isArray(obj))
        {
            return obj.map(item => this.cloneAndRemoveKeys(item, keysToExclude));
        }
        else if (obj instanceof Date)
        {
            return obj.toISOString();
        }
        else if (obj && typeof obj === "object")
        {
            const clone: any = {};
            for (const key of Object.keys(obj))
            {
                if (!keysToExclude.includes(key))
                {
                    clone[key] = this.cloneAndRemoveKeys(obj[key], keysToExclude);
                }
            }
            return clone;
        }
        return obj;
    }

    public static compareJsonTextual(a: any,
                                     b: any): boolean
    {
        let aJson = a ? JSON.stringify(a) : "";
        let bJson = b ? JSON.stringify(b) : "";

        return aJson == bJson;
    }

    public static valuesAreUnique<T>(values: T[]): boolean
    {
        return new Set<T>(values).size === values?.length;
    }

    /**
     * Return format associated with count provided
     */
    public static pluralize(count: number,
                            pluralized: PluralizedValues)
    {
        if (count > 1) return pluralized.multipleFormat;
        if (count === 1) return pluralized.singularFormat;
        return pluralized.zeroFormat;
    }

    private static isVowel(char: string): boolean
    {
        return /[aeiouAEIOU]/.test(char);
    }

    /**
     *
     * Generates unique name given other names already used
     *
     * @param title
     * @param otherTitles
     */
    public static getUniqueTitle(title: string,
                                 otherTitles: string[]): string
    {
        if (!otherTitles) return null;
        if (!otherTitles.length) return title;
        let iterationNum = 0;

        if (/ copy [0-9]{1,2}$/.test(title))
        {
            let numStartIdx = title.lastIndexOf(" ") + 1;
            iterationNum    = +title.substring(numStartIdx);
            title           = iterationNum ? title.substring(0, numStartIdx - " copy ".length) : title;
        }
        else if (title.endsWith(" copy"))
        {
            iterationNum = 1;
            title        = title.substring(0, title.length - " copy".length);
        }

        for (let i = iterationNum; i < iterationNum + otherTitles.length + 1; i++)
        {
            let substTitle = title;
            if (i) substTitle = i > 1 ? `${title} copy ${i}` : `${title} copy`;
            let otherTitle = otherTitles.find((otherTitle) => otherTitle === substTitle);
            if (!otherTitle) return substTitle;
        }

        return null;
    }

    /**
     * Replace all occurrences of find in str.
     * @param str
     * @param find
     * @param replace
     */
    public static replaceAll(str: string,
                             find: string,
                             replace: string): string
    {
        let result = "";
        let pos    = 0;

        while (pos < str.length)
        {
            let nextMatch = str.indexOf(find, pos);
            if (nextMatch < 0)
            {
                break;
            }

            result += str.substr(pos, nextMatch - pos);
            result += replace;
            pos = nextMatch + find.length;
        }

        result += str.substr(pos);
        return result;
    }

    /**
     * Capitalize the first letter of all words
     * @param str
     */
    public static capitalizeFirstLetterAllWords(str: string)
    {
        if (str)
        {
            let pieces = str.split(" ");
            for (let i = 0; i < pieces.length; i++)
            {
                let j     = pieces[i].charAt(0)
                                     .toUpperCase();
                pieces[i] = j + pieces[i].substr(1);
            }
            return pieces.join(" ");
        }

        return null;
    }

    public static wrapPromises<T>(...promises: Promise<T>[]): Promise<T[]>
    {
        return Promise.all(promises);
    }

    public static async waitFor(readyFn: () => Promise<boolean>,
                                checkIntervalMs: number = 100,
                                maxWaitDuration: number = -1,
                                quitFn: () => boolean   = () => false)
    {
        let elapsed = 0;
        while (!(await readyFn()))
        {
            await Future.delayed(checkIntervalMs);
            elapsed += checkIntervalMs;
            if (quitFn() || maxWaitDuration > 0 && elapsed >= maxWaitDuration) break;
        }
    }

    public static async executeWithRetries(fn: () => Promise<boolean>,
                                           numRetries: number,
                                           delay: number           = 200,
                                           failureFn?: () => void,
                                           delayMultiplier: number = 1,
                                           withInitialDelay?: boolean): Promise<boolean>
    {
        const delayFn = async () =>
        {
            await Future.delayed(delay);
            delay *= delayMultiplier;
        };

        if (withInitialDelay) await delayFn();

        while (true)
        {
            if (await fn()) return true;

            if (numRetries-- <= 0) break;

            await delayFn();
        }

        if (failureFn) failureFn();

        return false;
    }

    public static sum(nums: number[]): number
    {
        let sum = 0;
        for (let num of nums) sum += num;

        return sum;
    }

    public static mean(nums: number[]): number
    {
        return nums ? UtilsService.sum(nums) / nums.length : null;
    }

    /**
     * returns index of target or bitwise complement of closest idx in case it's not found
     *
     * @param sortedArray
     * @param target
     * @param getValueFn
     */
    public static binarySearch<T>(sortedArray: T[],
                                  target: number,
                                  getValueFn: (item: T) => number): number
    {
        let low  = 0;
        let high = sortedArray.length - 1;

        while (low <= high)
        {
            let mid    = Math.floor((low + high) / 2);
            let midVal = getValueFn(sortedArray[mid]);

            if (midVal < target)
            {
                low = mid + 1;
            }
            else if (midVal > target)
            {
                high = mid - 1;
            }
            else
            {
                return mid;
            }
        }

        return -(low + 1);
    }

    /**
     * returns index of target or bitwise complement of closest idx in case it's not found
     *
     * @param sortedArray
     * @param target
     * @param sortedArrayLen
     */
    public static binarySearchForFloat64Array(sortedArray: Float64Array,
                                              sortedArrayLen: number,
                                              target: number): number
    {
        let low  = 0;
        let high = sortedArrayLen - 1;

        while (low <= high)
        {
            let mid    = Math.floor((low + high) / 2);
            let midVal = sortedArray[mid];

            if (midVal < target)
            {
                low = mid + 1;
            }
            else if (midVal > target)
            {
                high = mid - 1;
            }
            else
            {
                return mid;
            }
        }

        return -(low + 1);
    }

    public static mergeSortedArrays<T>(sortedArrays: T[][],
                                       compareFn: (a: T,
                                                   b: T) => number): T[]
    {
        switch (sortedArrays.length)
        {
            case 0:
                return [];

            case 1:
                return sortedArrays[0];

            case 2:
                return UtilsService.mergeSortedHelper(sortedArrays[0], sortedArrays[1], compareFn);

            default:
                let nextLevelLen = Math.floor((sortedArrays.length + 1) / 2);
                let nextLevel    = new Array<T[]>(nextLevelLen);
                for (let i = 0; i < nextLevelLen; i++)
                {
                    let a = sortedArrays[2 * i];
                    let b = sortedArrays[2 * i + 1];

                    nextLevel[i] = !b ? a : UtilsService.mergeSortedHelper(a, b, compareFn);
                }

                return UtilsService.mergeSortedArrays(nextLevel, compareFn);
        }
    }

    private static mergeSortedHelper<T>(a: T[],
                                        b: T[],
                                        compareFn: (a: T,
                                                    b: T) => number): T[]
    {
        a = a || [];
        b = b || [];

        let aLen = a.length;
        let bLen = b.length;
        let out  = new Array<T>(aLen + bLen);
        let aIdx = 0;
        let bIdx = 0;
        while (aIdx < aLen && bIdx < bLen)
        {
            let aVal   = a[aIdx];
            let bVal   = b[bIdx];
            let result = compareFn(aVal, bVal);
            if (result <= 0)
            {
                out[aIdx++ + bIdx] = aVal;
            }
            else
            {
                out[aIdx + bIdx++] = bVal;
            }
        }

        while (aIdx < aLen)
        {
            // Don't inline, otherwise we get the wrong index.
            let aVal           = a[aIdx];
            out[aIdx++ + bIdx] = aVal;
        }

        while (bIdx < bLen)
        {
            // Don't inline, otherwise we get the wrong index.
            let bVal           = b[bIdx];
            out[aIdx + bIdx++] = bVal;
        }

        return out;
    }

    /**
     * Get the portions of an href, broken down by the regex found at https://stackoverflow.com/a/21553982.
     * @param href {string} - The URL to parse.
     */
    public static parseUrl(href: string): ParsedUrl
    {
        return new ParsedUrl(href);
    }

    public runInsideAngular<T>(callback: () => T): T
    {
        return this.ngZone.run(callback);
    }

    public runOutsideAngular<T>(callback: () => T): T
    {
        return this.ngZone.runOutsideAngular(callback);
    }

    public setTimeoutOutsideAngular(callback: () => void,
                                    ms: number): any
    {
        return this.runOutsideAngular(() =>
                                      {
                                          return setTimeout(() =>
                                                            {
                                                                this.ngZone.run(() =>
                                                                                {
                                                                                    callback();
                                                                                });
                                                            }, ms);
                                      });
    }

    public clearTimeoutOutsideAngular(handle: any): void
    {
        if (handle == null) return;
        this.runOutsideAngular(() => window.clearTimeout(handle));
    }

    public setIntervalOutsideAngular(callback: () => void,
                                     ms: number): any
    {
        return this.runOutsideAngular(() =>
                                      {
                                          return setInterval(() =>
                                                             {
                                                                 this.ngZone.run(() =>
                                                                                 {
                                                                                     callback();
                                                                                 });
                                                             }, ms);
                                      });
    }

    public static extractKeysFromMap(data: Lookup<any>,
                                     skipNulls: boolean = false,
                                     ...skipKey: string[]): string[]
    {
        let res: string[] = [];

        if (data)
        {
            for (let key in data)
            {
                if (skipKey != null && skipKey.indexOf(key) >= 0) continue;

                if (skipNulls && data[key] == null) continue;

                res.push(key);
            }
        }

        return res;
    }

    public static isMapEmpty(data: Lookup<any>)
    {
        if (data)
        {
            for (let key in data)
            {
                return false;
            }
        }

        return true;
    }

    public static countKeysInMap(data: Lookup<any>,
                                 skipNulls: boolean = false,
                                 ...skipKey: string[]): number
    {
        let res = 0;

        if (data)
        {
            for (let key in data)
            {
                if (skipKey != null && skipKey.indexOf(key) >= 0) continue;

                if (skipNulls && data[key] == null) continue;

                res++;
            }
        }

        return res;
    }

    public static extractUniqueValues<I, O>(inputs: Iterable<I>,
                                            accessor: (val: I) => O): O[]
    {
        let unique = new Set<O>();

        if (inputs)
        {
            for (let v of inputs)
            {
                if (v)
                {
                    let o = accessor(v);
                    if (o)
                    {
                        unique.add(o);
                    }
                }
            }
        }

        return [...unique];
    }

    public static createBuckets<I>(inputs: Iterable<I>,
                                   accessor: (value: I) => string,
                                   result?: Lookup<I[]>): Lookup<I[]>
    {
        result ||= {};
        if (!inputs) return result;

        for (const val of inputs)
        {
            const bucket = LookupHelpers.ensureArray(result, accessor(val));
            bucket.push(val);
        }

        return result;
    }

    //--//

    public static replaceUnderscoresWithSpaces(s: string): string
    {
        return s?.replace(/_/g, " ");
    }

    public static spaceSeparateByCapitalization(s: string): string
    {
        if (!s) return s;

        const capitalIndices = [];
        for (let i = 1; i < s.length; i++)
        {
            const l = s[i];
            if (l >= "A" && l <= "Z") capitalIndices.push(i);
        }
        capitalIndices.push(s.length);

        let o           = "";
        let prevCapital = 0;
        for (const capitalIndex of capitalIndices)
        {
            o += `${s.substring(prevCapital, capitalIndex)} `;
            prevCapital = capitalIndex;
        }

        return o.substring(0, o.length - 1);
    }

    //--//

    /**
     * Split a line into two parts, based on position of white space.
     * @param s
     */
    public static splitTextIntoTwoLines(s: string): string[]
    {
        s = s.replace(/\s/g, " ");

        let words = s.split(" ");
        if (words.length > 1)
        {
            let lengthMidpoint = s.length / 2;
            let lengthLeft     = -1;
            let whole          = words.join(" ");

            for (let word of words)
            {
                lengthLeft += word.length + 1;

                if (lengthLeft >= lengthMidpoint)
                {
                    let left  = whole.substr(0, lengthLeft);
                    let right = whole.substr(lengthLeft + 1);

                    return [
                        left,
                        right
                    ];
                }
            }
        }

        return [];
    }

    /**
     * Converts the rest of the arguments to a comma-separated line in the 'output' array.
     *
     * @param {string[]} output Target array
     * @param args
     */
    public static appendAsCommaSeparatedLine(output: string[],
                                             ...args: any[])
    {
        let line = "";

        for (let i in args)
        {
            let val = args[i] || "";
            let txt = val.toString();

            txt = UtilsService.replaceAll(txt, "\r", " ");
            txt = UtilsService.replaceAll(txt, "\n", " ");

            if (line.length > 0)
            {
                line += ",";
            }

            line += UtilsService.replaceAll(txt, ",", ";");
        }

        output.push(line);
    }

    public static afterJsonParse<T>(obj: T,
                                    constructor: { new(): T }): T
    {
        // console.debug(`Looking for fixup function for '${constructor.name}'`);

        //
        // We have an instance method on each class that requires fixups.
        // We'll walk the prototype hierarchy looking for it.
        // The method then returns the static function that will perform the actual fixup.
        //
        let proto = constructor.prototype;
        while (proto)
        {
            let fixupFunction = proto.getFixupPrototypeFunction;
            if (fixupFunction instanceof Function)
            {
                let func = fixupFunction();
                func(obj);
                // console.debug(`Found fixup function '${constructor.name}' -> ${func}`);
                break;
            }

            let parentProto = Object.getPrototypeOf(proto);
            if (parentProto === Object.prototype)
            {
                break;
            }

            proto = parentProto;
        }

        return obj;
    }

    public static getEnumNames(e: any): string[]
    {
        return Object.keys(e)
                     .filter((key) => isNaN(+key));
    }

    public static getEnumValues<T>(e: any): T[]
    {
        let names = UtilsService.getEnumNames(e);
        return names.map((n) => e[n]);
    }

    public static getEnumValue<T>(e: any,
                                  key: string): T
    {
        return <T>e[key];
    }

    public static parseEnumValue<T>(e: any,
                                    value: string): T
    {
        if (!e || value == null)
        {
            return null;
        }

        // Our enum style prefers key === value, so check that first and avoid caching when possible.
        if (Object.prototype.hasOwnProperty.call(e, value) && e[value] === value)
        {
            return <T>e[value];
        }

        let enumValues = this.s_enumValueCache.get(e);
        if (!enumValues)
        {
            enumValues = new Set(this.getEnumValues<string>(e));
            this.s_enumValueCache.set(e, enumValues);
        }

        return enumValues.has(value) ? <T>value : null;
    }

    public static optionsFromEnum<T>(e: any): ControlOption<T>[]
    {
        let res = [];

        for (let name of UtilsService.getEnumNames(e))
        {
            const label = this.capitalizeFirstLetterAllWords(this.replaceUnderscoresWithSpaces(name));
            res.push(new ControlOption<T>(UtilsService.getEnumValue(e, name), label));
        }

        return res;
    }

    public static parseComputedStyle(computedStyle: string,
                                     unit: string): number
    {
        let unitIdx = computedStyle.indexOf(unit);
        if (unitIdx === -1) return 0;

        computedStyle = computedStyle.substring(0, unitIdx) + computedStyle.substring(unitIdx + unit.length);
        return parseFloat(computedStyle.trim());
    }

    public static formatCssValue(val: number | string): string
    {
        if (typeof val === "number")
        {
            return `${val}px`;
        }

        return val;
    }

    public static numLinesOccupied(inlineElem: HTMLElement): number
    {
        return Math.floor(inlineElem.offsetHeight / UtilsService.parseComputedStyle(getComputedStyle(inlineElem).lineHeight, "px"));
    }

    public static clamp(min: number,
                        max: number,
                        value: number): number
    {
        return Math.max(Math.min(value, max), min);
    }

    //--//

    public static predictLength(label: string,
                                fontSize: number): number
    {
        let numChars = label?.length || 0;
        return PxPerCharacter * fontSize * numChars;
    }

    private static computeSmartLength(lengths: number[],
                                      maxZ: number,
                                      padding: number): number
    {
        if (isNaN(maxZ ?? NaN)) maxZ = 2;
        if (isNaN(padding ?? NaN)) padding = 30;

        // Find mean length
        let mean = 0;
        for (let length of lengths) mean += length;
        mean = mean / lengths.length;

        // Find standard deviation
        let cumMeanDiffSquared = 0;
        for (let length of lengths) cumMeanDiffSquared += (length - mean) * (length - mean);
        let stdDev = Math.sqrt(cumMeanDiffSquared / lengths.length);

        // Find largest length that is inside provided z-score
        let maxAcceptable     = maxZ * stdDev + mean;
        let largestAcceptable = 0;
        for (let length of lengths)
        {
            if (length <= maxAcceptable && length > largestAcceptable) largestAcceptable = length;
        }

        if (padding > 0) largestAcceptable += padding;

        return largestAcceptable;
    }

    public static smartLength(labels: string[],
                              measureFn: (label: string) => number,
                              padding: number,
                              autoSizeThreshold: number)
    {
        let lengths = labels.map((label) => measureFn(label));
        return UtilsService.computeSmartLength(lengths, autoSizeThreshold, padding);
    }

    public static smartTreeLength(nodes: LabeledTreeNode[],
                                  measureFn: (label: string) => number,
                                  offsetPerDepth: number,
                                  padding: number,
                                  autoSizeThreshold: number)
    {
        offsetPerDepth ||= 0;

        let hasNesting = false;
        const workFn   = (node: LabeledTreeNode,
                          depth: number) =>
        {
            hasNesting ||= !!node.children?.length;
            return Math.max(0, depth * offsetPerDepth) + measureFn(node.label) + (node.additionalWidth || 0);
        };

        let lengths = this.treeFlattenMap(nodes, workFn);
        if (hasNesting) padding += 30; // add 30px for the arrow

        return UtilsService.computeSmartLength(lengths, autoSizeThreshold, padding);
    }

    public static predictSmartLength(labels: string[],
                                     fontSize: number,
                                     padding: number,
                                     autoSizeThreshold: number): number
    {
        return UtilsService.smartLength(labels, (label: string) => UtilsService.predictLength(label, fontSize), padding, autoSizeThreshold);
    }

    public static predictSmartTreeLength(nodes: LabeledTreeNode[],
                                         fontSize: number,
                                         offsetPerDepth: number,
                                         padding: number,
                                         autoSizeThreshold: number): number
    {
        return UtilsService.smartTreeLength(nodes, (label: string) => UtilsService.predictLength(label, fontSize), offsetPerDepth, padding, autoSizeThreshold);
    }

    public static flatten<T extends TreeNode>(nodes: T[]): T[]
    {
        return this.treeFlattenMap(nodes, (node: TreeNode) => <T>node);
    }

    private static treeFlattenMap<T extends TreeNode, U>(nodes: T[],
                                                         workFn: (node: T,
                                                                  depth: number) => U,
                                                         depth: number = 0): U[]
    {
        const values: U[] = [];
        this.treeFlattenMapInto(nodes, workFn, values, depth);
        return values;
    }

    private static treeFlattenMapInto<T extends TreeNode, U>(nodes: T[],
                                                             workFn: (node: T,
                                                                      depth: number) => U,
                                                             values: U[],
                                                             depth: number): void
    {
        for (let i = 0; i < nodes?.length; i++)
        {
            const node = nodes[i];
            values.push(workFn(node, depth));

            if (node.children?.length > 0)
            {
                this.treeFlattenMapInto(<T[]>node.children, workFn, values, depth + 1);
            }
        }
    }

    // Removes nodes in-place; keeps a node if it matches `fn` or if `onlyRequireDescendants` is true and some descendants are valid
    public static filterInPlace<T extends TreeNode>(nodes: T[],
                                                    fn: (node: T) => boolean,
                                                    onlyRequireDescendants: boolean): T[]
    {
        if (nodes)
        {
            for (let i = 0; i < nodes.length; i++)
            {
                const node = nodes[i];
                if (node)
                {
                    const matches = fn(node);
                    if (!matches && !onlyRequireDescendants)
                    {
                        // local node doesn't match and descendant matches will not suffice: don't `continue` and fall into splicing
                    }
                    else
                    {
                        UtilsService.filterInPlace(<T[]>node.children, fn, onlyRequireDescendants);
                        if (matches || !!node.children?.length) continue;
                    }
                }

                nodes.splice(i--, 1);
            }
        }
        return nodes;
    }

    // Non-mutating variant of filterInPlace; keeps a node if it matches `fn`
    // or if `onlyRequireDescendants` is true and some descendants are valid.
    public static filter<T extends TreeNode>(nodes: T[],
                                             fn: (node: T) => boolean,
                                             onlyRequireDescendants: boolean): T[]
    {
        const results: T[] = [];
        for (const node of nodes || [])
        {
            if (!node) continue;

            const filteredChildren = UtilsService.filter(<T[]>node.children, fn, onlyRequireDescendants);
            const matches          = fn(node);
            if (!matches && (!onlyRequireDescendants || !filteredChildren.length)) continue;

            const copiedNode = Object.assign(Object.create(Object.getPrototypeOf(node)), node);
            copiedNode.children = filteredChildren;
            results.push(copiedNode);
        }

        return results;
    }

    public static findRecursive<T extends TreeNode>(nodes: T[],
                                                    fn: (node: T) => boolean): T | null
    {
        for (const node of nodes || [])
        {
            if (fn(node)) return node;
            const nestedResult = UtilsService.findRecursive(<T[]>node.children, fn);
            if (nestedResult) return nestedResult;
        }

        return null;
    }

    public static forEachRecursive<T extends TreeNode>(nodes: T[],
                                                       fn: (node: T) => any)
    {
        for (const node of nodes || [])
        {
            fn(node);
            UtilsService.forEachRecursive(<T[]>node.children, fn);
        }
    }

    public static mapRecursive<T extends TreeNode, U extends TreeNode>(nodes: T[],
                                                                       mapFn: (node: T) => U): U[]
    {
        return nodes?.map(pre =>
                          {
                              if (!pre) return null;

                              const post    = mapFn(pre);
                              post.children = UtilsService.mapRecursive(<T[]>pre.children, mapFn);
                              return post;
                          });
    }

    public static ensureBaseValue<K, V>(map: Map<K, V>,
                                        itemId: K,
                                        base: () => V): V
    {
        let value = map.get(itemId);
        if (!value)
        {
            value = base();
            map.set(itemId, value);
        }
        return value;
    }
}

export class PluralizedValues
{
    get valid(): boolean
    {
        return !!this.zeroFormat && !!this.singularFormat && !!this.multipleFormat;
    }

    constructor(public readonly zeroFormat: string,
                public readonly singularFormat: string,
                public readonly multipleFormat: string)
    {}
}

export interface MouseTouchEvent
{
    target: EventTarget;
    offsetX: number;
    offsetY: number;
    clientX: number;
    clientY: number;
    pageY: number;
    preventDefault: () => void;
    stopPropagation: () => void;
}

export function fromTouchEvent(e: TouchEvent,
                               target: HTMLElement): MouseTouchEvent
{
    let touch: Touch;
    for (let i = 0; i < e.touches.length; i++)
    {
        if (e.touches[i].target === target)
        {
            touch = e.touches[i];
            break;
        }
    }
    if (!touch) return null;

    let rect = target.getBoundingClientRect();
    return {
        target         : touch.target,
        offsetX        : touch.clientX - rect.x,
        offsetY        : touch.clientY - rect.y,
        clientX        : touch.clientX,
        clientY        : touch.clientY,
        pageY          : touch.pageY,
        preventDefault : () => e.preventDefault(),
        stopPropagation: () => e.stopPropagation()
    };
}

export type EnumOrString = number | string;
export type Lookup<T, U extends EnumOrString = string> = { [key in U]?: T };

export class LookupHelpers
{
    public static toMap<T>(lookup: Lookup<T>,
                           skipNulls: boolean = true): Map<string, T>
    {
        const map = new Map();
        for (const id in lookup)
        {
            const val = lookup[id];
            if (val != null || !skipNulls) map.set(id, val);
        }
        return map;
    }

    public static keys<U extends EnumOrString>(lookup: Lookup<any, U>): U[]
    {
        return <U[]>Object.keys(lookup);
    }

    public static values<T>(lookup: Lookup<T>): T[]
    {
        return Object.values(lookup);
    }

    public static mapEntries<K extends EnumOrString, V, O>(lookup: Lookup<V, K>,
                                                           mapFn: (key: K,
                                                                   value: V) => O): O[]
    {
        const out = [];
        for (const key in lookup) out.push(mapFn(key, lookup[key]));
        return out;
    }

    public static ensureBaseState<T, U extends EnumOrString>(lookup: Lookup<T, U>,
                                                             itemId: U,
                                                             base: () => T): T
    {
        let item = lookup[itemId];
        if (!item)
        {
            item           = base();
            lookup[itemId] = item;
        }

        return item;
    }

    public static ensureArray<T, U extends EnumOrString>(lookup: Lookup<T[], U>,
                                                         itemId: U): T[]
    {
        return LookupHelpers.ensureBaseState(lookup, itemId, (): T[] => []);
    }

    public static pushToArray<T>(lookup: Lookup<T[]>,
                                 itemId: string,
                                 item: T)
    {
        LookupHelpers.ensureArray(lookup, itemId)
                     .push(item);
    }

    public static some<T>(lookup: Lookup<T>,
                          someFn: (key: string,
                                   value: T) => boolean): boolean
    {
        for (const id in lookup)
        {
            if (someFn(id, lookup[id])) return true;
        }

        return false;
    }

    public static from<T, U extends EnumOrString>(input: Iterable<T>,
                                                  keyFn: (entry: T) => U): Lookup<T, U>
    {
        const lookup: Lookup<T, U> = {};
        for (const entry of input) lookup[keyFn(entry)] = entry;
        return lookup;
    }
}

export class ParsedUrl
{
    public protocol: string;
    public host: string;
    public hostname: string;
    public port: string;
    public pathname: string;
    public search: string;
    public hash: string;

    constructor(href: string)
    {
        if (href)
        {
            let match = href.match(/^(https?\:)\/\/(([^:\/?#]*)(?:\:([0-9]+))?)([\/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/);
            if (match)
            {
                this.protocol = match[1];
                this.host     = match[2];
                this.hostname = match[3];
                this.port     = match[4];
                this.pathname = match[5];
                this.search   = match[6];
                this.hash     = match[7];
            }
        }
    }
}

export interface Identified
{
    id: string | number;
}

interface Labeled
{
    label: string;
}

export interface TreeNode
{
    children?: TreeNode[];
}

export interface LabeledTreeNode extends TreeNode,
                                         Labeled
{
    additionalWidth?: number;
    children: LabeledTreeNode[];
}

export interface Change
{
    path: string[];
    a: any;
    b: any;
}

// for 2000 characters long sample text, for every 1px font size increase, there's an avg increase of 996px draw length
// used 2000 characters for test because 'z' is used, on average, once in every 2000 characters
export const PxPerCharacter = 996 / 2000; // developed using "'Helvetica Neue', 'Helvetica', 'Arial', sans-serif" font family and "normal" style

export class TypeDispatcher<M, C>
{
    private m_typeToConstructor = new Map<Type<M>, Type<C>>();

    register<T extends Type<M>>(modelType: T): any
    {
        const self = this;
        return function (target: any)
        {
            self.m_typeToConstructor.set(modelType.prototype, target);
            return target;
        };
    }

    create(model: M): C
    {
        const ctr = this.m_typeToConstructor.get(Object.getPrototypeOf(model));
        if (ctr)
        {
            return new ctr(model);
        }

        return null;
    }
}
